/** Lokales Datum als YYYY-MM-DD (für type="date"). */
export function todayLocal(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function addDaysLocal(isoDate: string, days: number): string {
  const parts = isoDate.split("-").map(Number);
  const y = parts[0] ?? 0;
  const m = parts[1] ?? 1;
  const d = parts[2] ?? 1;
  const date = new Date(y, m - 1, d + days);
  const ny = date.getFullYear();
  const nm = String(date.getMonth() + 1).padStart(2, "0");
  const nd = String(date.getDate()).padStart(2, "0");
  return `${ny}-${nm}-${nd}`;
}

/** Lokales Datum aus YYYY-MM-DD (ohne UTC-Verschiebung). */
export function parseLocalIsoDate(isoDate: string): Date {
  const parts = isoDate.split("-").map(Number);
  const y = parts[0] ?? 0;
  const m = parts[1] ?? 1;
  const d = parts[2] ?? 1;
  return new Date(y, m - 1, d);
}

const DATE_SHORT = new Intl.DateTimeFormat("de-DE", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

/** Kurzes Datum, z. B. „24.05.2026“. */
export function formatIsoDateShort(isoDate: string): string {
  return DATE_SHORT.format(parseLocalIsoDate(isoDate));
}

/** Kurzlabel für Tages-Chips: „Heute“ oder „26.05.2026“. */
export function formatArrivalDayChip(isoDate: string, today: string): string {
  if (isoDate === today) return "Heute";
  return formatIsoDateShort(isoDate);
}

/** Lesbares Datum, z. B. „24.05.2026“. */
export function formatArrivalDayLong(isoDate: string): string {
  return formatIsoDateShort(isoDate);
}

const DATE_TIME_SHORT = new Intl.DateTimeFormat("de-DE", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

/** Datum und Uhrzeit, z. B. „24.05.2026, 14:30“. */
export function formatDateTimeShort(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return DATE_TIME_SHORT.format(d);
}

/** Nächte zwischen Anreise (inkl.) und Abreise (exklusiv). */
export function countNightsLocal(arrival: string, departure: string): number {
  const a = parseLocalIsoDate(arrival);
  const d = parseLocalIsoDate(departure);
  const nights = Math.round((d.getTime() - a.getTime()) / 86_400_000);
  return nights > 0 ? nights : 0;
}

/** Verschiebt Anreise/Abreise um dayOffset Tage, Übernachtungsdauer bleibt gleich. */
export function shiftBookingPeriod(
  arrival: string,
  departure: string,
  dayOffset: number,
): { arrival: string; departure: string } {
  const nights = countNightsLocal(arrival, departure);
  const newArrival = addDaysLocal(arrival, dayOffset);
  return {
    arrival: newArrival,
    departure: addDaysLocal(newArrival, nights),
  };
}

/** Lesbares Zeitraum-Label für Alternativ-Vorschläge. */
export function formatBookingPeriodShort(arrival: string, departure: string, nights: number): string {
  return `${formatIsoDateShort(arrival)} → ${formatIsoDateShort(departure)} (${nights} ${nights === 1 ? "Nacht" : "Nächte"})`;
}

/** Aufeinanderfolgende Kalendertage ab start (inklusive), length Tage. */
export function consecutiveDaysFrom(start: string, length: number): string[] {
  const days: string[] = [];
  let cur = start;
  for (let i = 0; i < length; i++) {
    days.push(cur);
    cur = addDaysLocal(cur, 1);
  }
  return days;
}

/** Erzwingt min auf dem Client (iOS ignoriert type="date"-min teilweise). */
export function clampDateToMin(isoDate: string, min: string): string {
  if (!isoDate || !min) return isoDate;
  return isoDate < min ? min : isoDate;
}

export function toLocalIsoDate(year: number, month: number, day: number): string {
  const m = String(month).padStart(2, "0");
  const d = String(day).padStart(2, "0");
  return `${year}-${m}-${d}`;
}

export function isIsoDateSelectable(iso: string, min?: string, max?: string): boolean {
  if (min && iso < min) return false;
  if (max && iso > max) return false;
  return true;
}

export interface CalendarMonthView {
  year: number;
  month: number;
}

export function calendarMonthFromIso(iso: string): CalendarMonthView {
  const [y, m] = iso.split("-").map(Number);
  return { year: y ?? 0, month: m ?? 1 };
}

/** Monat für Kalenderansicht: gewähltes Datum, sonst min, sonst heute. */
export function initialCalendarMonth(value: string, min?: string): CalendarMonthView {
  if (value) return calendarMonthFromIso(value);
  if (min) return calendarMonthFromIso(min);
  return calendarMonthFromIso(todayLocal());
}

export function shiftCalendarMonth(view: CalendarMonthView, delta: number): CalendarMonthView {
  const date = new Date(view.year, view.month - 1 + delta, 1);
  return { year: date.getFullYear(), month: date.getMonth() + 1 };
}

export interface CalendarDayCell {
  iso: string;
  day: number;
  inMonth: boolean;
  disabled: boolean;
}

const CALENDAR_WEEKDAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"] as const;

export function calendarWeekdayLabels(): readonly string[] {
  return CALENDAR_WEEKDAYS;
}

const CALENDAR_MONTH_TITLE = new Intl.DateTimeFormat("de-DE", {
  month: "long",
  year: "numeric",
});

export function formatCalendarMonthTitle(view: CalendarMonthView): string {
  return CALENDAR_MONTH_TITLE.format(new Date(view.year, view.month - 1, 1));
}

/** 6×7-Raster (Mo–So), inkl. Nachbar-Monatstage. */
export function buildCalendarMonthGrid(
  view: CalendarMonthView,
  min?: string,
  max?: string,
): CalendarDayCell[] {
  const { year, month } = view;
  const first = new Date(year, month - 1, 1);
  const startOffset = (first.getDay() + 6) % 7;
  const gridStart = new Date(year, month - 1, 1 - startOffset);

  const cells: CalendarDayCell[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + i);
    const iso = toLocalIsoDate(d.getFullYear(), d.getMonth() + 1, d.getDate());
    cells.push({
      iso,
      day: d.getDate(),
      inMonth: d.getMonth() === month - 1,
      disabled: !isIsoDateSelectable(iso, min, max),
    });
  }
  return cells;
}

/** Vorheriger Monat nur, wenn darin noch wählbare Tage liegen. */
export function canShowPreviousCalendarMonth(view: CalendarMonthView, min?: string): boolean {
  if (!min) return true;
  const prev = shiftCalendarMonth(view, -1);
  const lastDay = new Date(prev.year, prev.month, 0).getDate();
  const lastIso = toLocalIsoDate(prev.year, prev.month, lastDay);
  return lastIso >= min;
}

export type DateValidationError = "past_arrival" | "departure_before_arrival" | null;

export function validateBookingDates(arrival: string, departure: string): DateValidationError {
  const today = todayLocal();
  if (arrival && arrival < today) return "past_arrival";
  if (arrival && departure && departure <= arrival) return "departure_before_arrival";
  return null;
}

export function dateValidationMessage(code: DateValidationError): string | null {
  switch (code) {
    case "past_arrival":
      return "Anreise darf nicht in der Vergangenheit liegen.";
    case "departure_before_arrival":
      return "Abreise muss nach der Anreise liegen.";
    default:
      return null;
  }
}
